"use strict";
/**
 * Structured logger for the OwlCat fleet (@owlcat/logger).
 *
 * ALWAYS-ON structured JSON in production: one JSON line per event to stdout
 * (Loki-parseable, compatible with structlog from the Python workers). In
 * development: human-readable to stdout + JSON lines to a file (default
 * /tmp/portal-dev.log) so external tools (Claude Code, tail -f) can read a
 * running process without capturing stdout.
 *
 * Pure — no next/react/app coupling. Safe in Node, Edge and browser bundles
 * (node-only bits are loaded lazily and no-op elsewhere). Env-tunable:
 *   LOG_LEVEL      debug|info|warn|error (default debug — emit everything)
 *   LOG_FILE       dev JSON-lines path (PORTAL_LOG_FILE also honoured)
 *   LOG_MAX_BYTES  rotation threshold (PORTAL_LOG_MAX_BYTES also honoured)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LOG_FILE = exports.logger = void 0;
exports.createLogger = createLogger;
const redact_1 = require("./redact");
let _fs;
function getFs() {
    if (_fs !== undefined)
        return _fs;
    const isNode = typeof process !== 'undefined'
        && !!process.versions?.node
        && process.env.NEXT_RUNTIME !== 'edge';
    if (!isNode)
        return (_fs = null);
    try {
        // Load 'fs' WITHOUT a static require or eval — both are rejected by the
        // Edge-runtime build even though this branch only runs in Node. Node 22.3+
        // exposes process.getBuiltinModule for exactly this. Missing → file log
        // silently no-ops (stdout logging is unaffected).
        const getBuiltinModule = process.getBuiltinModule;
        _fs = (getBuiltinModule ? getBuiltinModule('fs') : null);
    }
    catch {
        _fs = null;
    }
    return _fs ?? null;
}
function argsToContext(args) {
    if (args.length === 0)
        return undefined;
    if (args.length === 1) {
        const a = args[0];
        if (a instanceof Error)
            return { error: `${a.message}\n${a.stack ?? ''}` };
        if (a && typeof a === 'object' && !Array.isArray(a))
            return a;
        return { detail: a };
    }
    return {
        detail: args.map((a) => a instanceof Error ? `${a.message}\n${a.stack ?? ''}` : a),
    };
}
const isProduction = process.env.NODE_ENV === 'production';
const LOG_FILE = process.env.LOG_FILE || process.env.PORTAL_LOG_FILE || '/tmp/portal-dev.log';
exports.LOG_FILE = LOG_FILE;
// Fleet service tag: stamped into EVERY log line as `service` so Grafana/Loki
// can split the shared log stream by app (journald only labels by systemd unit;
// this tags the JSON body itself). Set SERVICE_NAME (or the OTel-standard
// OTEL_SERVICE_NAME) per app, e.g. SERVICE_NAME=shop-storefront. Absent → no
// field emitted (unchanged legacy behaviour).
const SERVICE_NAME = process.env.SERVICE_NAME || process.env.OTEL_SERVICE_NAME || undefined;
// Severity threshold — lines below LOG_LEVEL are dropped. Default 'debug' emits
// everything (identical to the historic always-on behaviour).
const LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
const THRESHOLD = LEVEL_WEIGHT[process.env.LOG_LEVEL ?? 'debug'] ?? LEVEL_WEIGHT.debug;
// Size-based rotation: history survives restarts (no truncate-on-start), the
// file never grows unbounded. current -> .1 -> .2, older dropped.
const MAX_LOG_BYTES = Number(process.env.LOG_MAX_BYTES || process.env.PORTAL_LOG_MAX_BYTES || 10 * 1024 * 1024);
const KEEP_ROTATIONS = 2;
let writesSinceCheck = 0;
function rotateIfNeeded(fs) {
    // stat() per write is wasteful — check every 200 writes.
    if (writesSinceCheck++ % 200 !== 0)
        return;
    try {
        if (fs.statSync(LOG_FILE).size < MAX_LOG_BYTES)
            return;
        try {
            fs.unlinkSync(`${LOG_FILE}.${KEEP_ROTATIONS}`);
        }
        catch { /* may not exist */ }
        for (let i = KEEP_ROTATIONS - 1; i >= 1; i--) {
            try {
                fs.renameSync(`${LOG_FILE}.${i}`, `${LOG_FILE}.${i + 1}`);
            }
            catch { /* may not exist */ }
        }
        fs.renameSync(LOG_FILE, `${LOG_FILE}.1`);
    }
    catch {
        // Missing file / permission — best-effort
    }
}
function writeToFile(entry) {
    const fs = getFs();
    if (!fs)
        return; // Edge / browser — no filesystem, stdout logging still works
    try {
        rotateIfNeeded(fs);
        fs.appendFileSync(LOG_FILE, JSON.stringify(entry) + '\n');
    }
    catch {
        // Best-effort — don't crash on file write errors
    }
}
function currentTrace() {
    // Lazy require to avoid a hard node:async_hooks dependency in Edge runtime.
    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        return require('./traceContext').currentTraceId();
    }
    catch {
        return undefined;
    }
}
function createLogger(defaultContext = {}) {
    function log(level, message, context) {
        if (LEVEL_WEIGHT[level] < THRESHOLD)
            return;
        const traceId = currentTrace();
        // Every field AND the message pass the secret redactor (./redact.ts) — URLs with ?key=,
        // Bearer headers, error messages / stacks, secret-named fields never reach stdout or the file.
        const merged = (0, redact_1.redactValue)({ ...defaultContext, ...(traceId ? { traceId } : {}), ...context });
        message = (0, redact_1.redact)(message);
        const entry = {
            timestamp: new Date().toISOString(),
            level,
            ...(SERVICE_NAME ? { service: SERVICE_NAME } : {}),
            event: message,
            ...merged,
        };
        if (isProduction) {
            // JSON to stdout — compatible with structlog from portal-workers
            const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
            fn(JSON.stringify(entry));
        }
        else {
            // Human-readable to stdout
            const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : level === 'debug' ? console.debug : console.log;
            const prefix = Object.keys(merged).length ? ` ${JSON.stringify(merged)}` : '';
            fn(`[${level.toUpperCase()}] ${message}${prefix}`);
            // JSON lines to file (for external tools)
            writeToFile(entry);
        }
    }
    return {
        debug: (msg, ...args) => log('debug', msg, argsToContext(args)),
        info: (msg, ...args) => log('info', msg, argsToContext(args)),
        warn: (msg, ...args) => log('warn', msg, argsToContext(args)),
        error: (msg, ...args) => log('error', msg, argsToContext(args)),
        child: (childCtx) => createLogger({ ...defaultContext, ...childCtx }),
    };
}
exports.logger = createLogger();
